import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import Home from "./pages/Home";
import Opportunities from "./pages/Opportunities";
import Industries from "./pages/Industries";
import Intelligence from "./pages/Intelligence";
import Events from "./pages/Events";
import Membership from "./pages/Membership";
import Members from "./pages/Members";
import Connections from "./pages/Connections";
import PostRFQ from "./pages/PostRFQ";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import PayLink from "./pages/PayLink";
import ScanCard from "./pages/ScanCard";
import VerifyMember from "./pages/VerifyMember";
import Dashboard from "./pages/Dashboard";
import PaymentSuccess from "./pages/PaymentSuccess";
import AdminLogin from "./pages/AdminLogin";
import AdminLayout from "./admin/AdminLayout";
import AdminOverview from "./admin/Overview";
import AdminSearch from "./admin/GlobalSearch";
import AdminPlans from "./admin/AdminPlans";
import AdminRegisterCompany from "./admin/AdminRegisterCompany";
import AdminCreateMembership from "./admin/AdminCreateMembership";
import {
  AdminActivity, AdminCompanies, AdminContacts, AdminVerification, AdminSources,
  AdminScores, AdminMarket, AdminInsights, AdminCampaigns, AdminInvitations, AdminQueue,
  AdminDelivery, AdminResponses, AdminSuppression, AdminProspects, AdminApplications,
  AdminApproval, AdminMembers, AdminAutomation, AdminEmailInfra, AdminWebhooks,
  AdminSecurity, AdminAudit, AdminSettings,
} from "./admin/pages";
import CapitalAdvisory from "./pages/CapitalAdvisory";
import AdminOutreach from "./pages/AdminOutreach";
import InvitePage from "./pages/InvitePage";
import OptOut from "./pages/OptOut";
import Register from "./pages/Register";
import OrganiserRegister from "./pages/OrganiserRegister";
import OrganiserLogin from "./pages/OrganiserLogin";
import OrganiserDashboard from "./pages/OrganiserDashboard";
import AdminEvents from "./pages/AdminEvents";
import MembershipAdvantage from "./pages/MembershipAdvantage";
import SecureRegister from "./pages/SecureRegister";
import About from "./pages/About";
import Contact from "./pages/Contact";
import AdminPartners from "./pages/AdminPartners";
import NotFound from "./pages/NotFound";
import RefRedirect from "./pages/RefRedirect";
import PartnerRegister from "./pages/PartnerRegister";
import PartnerLogin from "./pages/PartnerLogin";
import PartnerDashboard from "./pages/PartnerDashboard";
import AdminReferrals from "./admin/AdminReferrals";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function Chrome({ children }) {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return <>{children}</>;
  return (
    <>
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <div className="min-h-screen bg-navy-950 flex flex-col">
          <Chrome>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/opportunities" element={<Opportunities />} />
              <Route path="/industries" element={<Industries />} />
              <Route path="/intelligence" element={<Intelligence />} />
              <Route path="/events" element={<Events />} />
              <Route path="/events/organiser/register" element={<OrganiserRegister />} />
              <Route path="/events/organiser/login" element={<OrganiserLogin />} />
              <Route path="/events/organiser/dashboard" element={<OrganiserDashboard />} />
              <Route path="/ref/:code" element={<RefRedirect />} />
              <Route path="*" element={<NotFound />} />
              <Route path="/membership" element={<Membership />} />
              <Route path="/members" element={<Members />} />
              <Route path="/connections" element={<Connections />} />
              <Route path="/rfq" element={<PostRFQ />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/pay/:linkId" element={<PayLink />} />
              <Route path="/scan" element={<ScanCard />} />
              <Route path="/verify/:memberId" element={<VerifyMember />} />
              <Route path="/join" element={<MembershipAdvantage />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminOverview />} />
                <Route path="search" element={<AdminSearch />} />
                <Route path="activity" element={<AdminActivity />} />
                <Route path="companies" element={<AdminCompanies />} />
                <Route path="contacts" element={<AdminContacts />} />
                <Route path="verification" element={<AdminVerification />} />
                <Route path="sources" element={<AdminSources />} />
                <Route path="scores" element={<AdminScores />} />
                <Route path="market" element={<AdminMarket />} />
                <Route path="insights" element={<AdminInsights />} />
                <Route path="campaigns" element={<AdminCampaigns />} />
                <Route path="invitations" element={<AdminInvitations />} />
                <Route path="queue" element={<AdminQueue />} />
                <Route path="delivery" element={<AdminDelivery />} />
                <Route path="responses" element={<AdminResponses />} />
                <Route path="suppression" element={<AdminSuppression />} />
                <Route path="prospects" element={<AdminProspects />} />
                <Route path="applications" element={<AdminApplications />} />
                <Route path="approval" element={<AdminApproval />} />
                <Route path="members" element={<AdminMembers />} />
                <Route path="plans" element={<AdminPlans />} />
                <Route path="register-company" element={<AdminRegisterCompany />} />
                <Route path="create-membership" element={<AdminCreateMembership />} />
                <Route path="referrals" element={<AdminReferrals />} />
                <Route path="automation" element={<AdminAutomation />} />
                <Route path="email-infra" element={<AdminEmailInfra />} />
                <Route path="webhooks" element={<AdminWebhooks />} />
                <Route path="security" element={<AdminSecurity />} />
                <Route path="audit" element={<AdminAudit />} />
                <Route path="settings" element={<AdminSettings />} />
              </Route>
              <Route path="/admin/events" element={<AdminEvents />} />
              <Route path="/admin/outreach" element={<AdminOutreach />} />
              <Route path="/invite/:token" element={<InvitePage />} />
              <Route path="/opt-out" element={<OptOut />} />
              <Route path="/capital-advisory" element={<CapitalAdvisory />} />
              <Route path="/register" element={<Register />} />
              <Route path="/secure-register" element={<SecureRegister />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/admin/partners" element={<AdminPartners />} />
              <Route path="/partner/register" element={<PartnerRegister />} />
              <Route path="/partner/login" element={<PartnerLogin />} />
              <Route path="/partner/dashboard" element={<PartnerDashboard />} />
            </Routes>
          </Chrome>
          <Toaster position="top-right" theme="dark" />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
